import React, { useState, useEffect, useCallback } from 'react';
import { useSelector } from 'react-redux';
import { selectedClientSelector } from '../../store/slices/selectedClient';
import { Link } from 'react-router-dom';
import axios from 'axios';

import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle
} from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Slider } from '../../components/ui/slider';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Badge } from '../../components/ui/badge';
import {
  Settings,
  Save,
  RotateCcw,
  ArrowLeft,
  Loader2,
  CheckCircle,
  AlertTriangle,
  Sliders,
  FileText,
  Monitor,
  Zap,
  Shield,
  Eye
} from 'lucide-react';

const API_BASE =
  process.env.REACT_APP_SEO_AUDIT_API ||
  'https://iw0nmalze1.execute-api.us-east-2.amazonaws.com/dev';

const DEFAULT_WEIGHTS = {
  technical: 25,
  onPage: 25,
  performance: 25,
  security: 15,
  accessibility: 10
};

const WEIGHT_META = {
  technical: {
    label: 'Technical SEO',
    icon: Monitor,
    color: '#3b82f6',
    description:
      'Site structure, crawlability, indexability, robots.txt, sitemaps'
  },
  onPage: {
    label: 'On-Page SEO',
    icon: FileText,
    color: '#8b5cf6',
    description:
      'Title tags, meta descriptions, H1 tags, image alt text, content quality'
  },
  performance: {
    label: 'Performance',
    icon: Zap,
    color: '#f59e0b',
    description:
      'Page load speed, Core Web Vitals, Google PageSpeed Insights score'
  },
  security: {
    label: 'Security',
    icon: Shield,
    color: '#10b981',
    description: 'SSL certificate, HTTPS enforcement, server signature'
  },
  accessibility: {
    label: 'Accessibility',
    icon: Eye,
    color: '#ec4899',
    description:
      'ARIA labels, color contrast, keyboard navigation, screen reader support'
  }
};

const SeoAuditSettings = () => {
  const { selectedClient } = useSelector(selectedClientSelector);
  const locationId = selectedClient?.id ? String(selectedClient.id) : '';

  const [weights, setWeights] = useState({ ...DEFAULT_WEIGHTS });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [error, setError] = useState('');

  // Total for validation — must sum to 100
  const totalWeight = Object.values(weights).reduce((a, b) => a + b, 0);
  const isValid = totalWeight === 100;

  // Load settings
  const loadSettings = useCallback(async () => {
    if (!locationId) return;
    setLoading(true);
    try {
      const res = await axios.get(
        `${API_BASE}/audit/settings?locationId=${locationId}`
      );
      const s = res.data?.settings;
      if (s && s.weights) {
        setWeights({
          technical: Math.round((s.weights.technical || 0.25) * 100),
          onPage: Math.round((s.weights.onPage || 0.25) * 100),
          performance: Math.round((s.weights.performance || 0.25) * 100),
          security: Math.round((s.weights.security || 0.15) * 100),
          accessibility: Math.round((s.weights.accessibility || 0.1) * 100)
        });
      }
    } catch (err) {
      console.warn('[SeoAuditSettings] Failed to load:', err.message);
    } finally {
      setLoading(false);
    }
  }, [locationId]);

  useEffect(() => {
    loadSettings();
  }, [loadSettings]);

  // Save settings
  const handleSave = async () => {
    if (!locationId || !isValid) return;
    setSaving(true);
    setError('');
    setSaved(false);
    try {
      await axios.post(`${API_BASE}/audit/settings`, {
        locationId,
        weights: {
          technical: weights.technical / 100,
          onPage: weights.onPage / 100,
          performance: weights.performance / 100,
          security: weights.security / 100,
          accessibility: weights.accessibility / 100
        }
      });
      setSaved(true);
      setTimeout(() => setSaved(false), 3000);
    } catch (err) {
      setError('Failed to save settings. Please try again.');
      console.error('[SeoAuditSettings] Save error:', err);
    } finally {
      setSaving(false);
    }
  };

  // Reset to defaults
  const handleReset = () => {
    setWeights({ ...DEFAULT_WEIGHTS });
    setSaved(false);
  };

  // Update a single weight
  const updateWeight = (key, value) => {
    setWeights((prev) => ({ ...prev, [key]: value }));
    setSaved(false);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
      </div>
    );
  }

  return (
    <div className="max-w-3xl mx-auto space-y-6 p-4 sm:p-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <Link to="/seo-audit">
            <Button variant="ghost" size="icon">
              <ArrowLeft className="h-5 w-5" />
            </Button>
          </Link>
          <div>
            <h1 className="text-2xl font-bold tracking-tight">
              <Settings className="inline h-6 w-6 mr-2 align-middle" />
              SEO Audit Settings
            </h1>
            <p className="text-sm text-muted-foreground mt-1">
              Configure scoring weights and crawl limits for{' '}
              <span className="font-medium text-foreground">
                {selectedClient?.clientName || 'this location'}
              </span>
            </p>
          </div>
        </div>

        {/* Action buttons */}
        <div className="flex items-center gap-2">
          <Button variant="outline" onClick={handleReset} className="gap-2">
            <RotateCcw className="h-4 w-4" />
            Reset
          </Button>
          <Button
            onClick={handleSave}
            disabled={!isValid || saving}
            className="gap-2"
            style={{
              backgroundColor: isValid ? '#3766a8' : undefined,
              color: isValid ? '#fff' : undefined
            }}>
            {saving ? (
              <Loader2 className="h-4 w-4 animate-spin" />
            ) : saved ? (
              <CheckCircle className="h-4 w-4" />
            ) : (
              <Save className="h-4 w-4" />
            )}
            {saving ? 'Saving...' : saved ? 'Saved!' : 'Save Settings'}
          </Button>
        </div>
      </div>

      {/* Error */}
      {error && (
        <div className="p-3 rounded-lg bg-red-50 dark:bg-red-950/30 border border-red-200 dark:border-red-800 text-red-700 dark:text-red-300 text-sm">
          {error}
        </div>
      )}

      {/* Scoring Weights Card */}
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <div>
              <CardTitle className="text-lg flex items-center gap-2">
                <Sliders className="h-5 w-5 text-purple-500" />
                Scoring Weights
              </CardTitle>
              <CardDescription>
                Adjust how each category contributes to the overall SEO score.
                Weights must total 100%.
              </CardDescription>
            </div>
            <Badge
              variant={totalWeight === 100 ? 'default' : 'destructive'}
              className="text-sm px-3 py-1">
              Total: {totalWeight}%{' '}
              {totalWeight === 100 ? (
                <CheckCircle className="inline h-3.5 w-3.5 ml-1" />
              ) : (
                <AlertTriangle className="inline h-3.5 w-3.5 ml-1" />
              )}
            </Badge>
          </div>
        </CardHeader>
        <CardContent className="space-y-6">
          {Object.entries(WEIGHT_META).map(([key, meta]) => {
            const Icon = meta.icon;
            const val = weights[key];
            return (
              <div key={key} className="space-y-2">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <div
                      className="h-9 w-9 rounded-lg flex items-center justify-center"
                      style={{ backgroundColor: `${meta.color}15` }}>
                      <Icon className="h-5 w-5" style={{ color: meta.color }} />
                    </div>
                    <div>
                      <div className="font-medium text-sm">{meta.label}</div>
                      <div className="text-xs text-muted-foreground">
                        {meta.description}
                      </div>
                    </div>
                  </div>
                  <div className="flex items-center gap-2 shrink-0">
                    <Input
                      type="number"
                      min={0}
                      max={100}
                      value={val}
                      onChange={(e) => {
                        const v = parseInt(e.target.value, 10);
                        if (!isNaN(v) && v >= 0 && v <= 100)
                          updateWeight(key, v);
                      }}
                      className="w-16 text-center text-sm h-8"
                    />
                    <span className="text-sm text-muted-foreground">%</span>
                  </div>
                </div>
                <Slider
                  value={[val]}
                  onValueChange={([v]) => updateWeight(key, v)}
                  max={100}
                  min={0}
                  step={5}
                  className="cursor-pointer"
                  style={{
                    '--slider-color': meta.color
                  }}
                />
              </div>
            );
          })}

          {totalWeight !== 100 && (
            <div className="p-3 rounded-lg bg-yellow-50 dark:bg-yellow-950/30 border border-yellow-200 dark:border-yellow-800 text-yellow-700 dark:text-yellow-300 text-sm flex items-center gap-2">
              <AlertTriangle className="h-4 w-4 shrink-0" />
              Weights must total exactly 100%. Currently {totalWeight}% (
              {totalWeight > 100
                ? `${totalWeight - 100}% over`
                : `${100 - totalWeight}% under`}
              ).
            </div>
          )}
        </CardContent>
      </Card>

      {/* How it works */}
      <Card>
        <CardHeader>
          <CardTitle className="text-lg">How Scoring Works</CardTitle>
        </CardHeader>
        <CardContent className="text-sm text-muted-foreground space-y-2">
          <p>
            The <strong>overall SEO score</strong> is calculated as a weighted
            average of the five category scores. Each category is scored 0–100
            based on the issues found during the audit.
          </p>
          <p>
            For example, with default weights, a site scoring 90 Technical, 80
            On-Page, 70 Performance, 100 Security, and 85 Accessibility would
            get: (90×0.25) + (80×0.25) + (70×0.25) + (100×0.15) + (85×0.10) =
            83.5 overall.
          </p>
        </CardContent>
      </Card>
    </div>
  );
};

export default SeoAuditSettings;
